import { useState, useEffect } from "react";
import Input from "../../../../../components/fields/input";
import Button from "../../../../../components/buttons/button";
import { useCreateteamsMutation, useUpdateteamsMutation } from "../../../../../store/Reducer/teams";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";

export default function AddUserModal({
  isOpen,
  closeModal,
  isEdit,
  rowData,
}) {
  const { t } = useTranslation();

  const [createteams, { isLoading }] = useCreateteamsMutation();
  const [updateteams, { isLoading: teamsupdating }] = useUpdateteamsMutation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    permission: 3, // Changed from 'permissions' to 'permission'
    password: "", // Added password
    isActive: true, // Default to active

  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEdit && rowData) {
        setFormData({
          name: rowData.name || "",
          email: rowData.email || "",
          password: "", // Leave empty for security
          isActive: rowData.isActive ?? true,
          permission: 3
        });
      } else {
        // Reset form data for creating a new user
        setFormData({
          name: "",
          email: "",
          password: "", // Reset password
          isActive: true, // Default to active
          permission: 3
        });
      }
    }
  }, [isEdit, rowData, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleActive = () => {
    setFormData((prev) => ({
      ...prev,
      isActive: !prev.isActive,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEdit && rowData?._id) {
      formData.id = rowData._id;
    }
    try {
      if (isEdit) {
        // The edit form hides the password field; an empty password fails
        // the backend's min-length check, so only send it when one was typed.
        const { password, ...rest } = formData;
        const response = await updateteams(password ? formData : rest).unwrap();
        toast.success("User updated successfully");
        closeModal(); // Close modal on success
      } else {
        const response = await createteams({ ...formData }).unwrap();
        toast.success("User created successfully");
        closeModal(); // Close modal on success
      }
    } catch (error) {
      console.error("Error submitting form:", error); // Log full error details

      // Ensure error messages are displayed correctly
      const errorMessage =
        error?.data?.message || // Message from Redux Toolkit's `unwrap`
        error?.message || // General error message
        "An unexpected error occurred"; // Fallback message

      toast.error(errorMessage);
    }
  };



  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 overflow-y-auto bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 sm:p-0">
          <div className="rounded-lg shadow-xl w-full max-w-2xl mx-auto h-full sm:h-auto sm:max-h-[90vh] flex flex-col">
            <div className="mx-auto w-full bg-white rounded-lg shadow-lg p-4 sm:px-6 sm:py-6">
              <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                {isEdit ? t("user_team_update_modal_title") : t("user_team_create_modal_title")}
              </h1>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Name */}
                  <Input
                    label="Enter Name"
                    type="text"
                    name="name"
                    placeholder="Enter Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />

                  {/* Email */}
                  <Input
                    label="Enter Email"
                    type="email"
                    name="email"
                    placeholder="Enter Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />





                  {!isEdit && ( // Only show password for adding a user

                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-shadow focus:outline-none"
                        placeholder="Enter Password"

                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                      </button>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 rounded-lg font-semibold text-sm border border-gray-300 text-gray-700 hover:bg-gray-100"
                  >
                    {t("universal_cancel_text")}
                  </button>

                  <Button
                    text={isEdit ? t("user_team_create_modal_btn_text_update_user") : t("user_team_create_modal_btn_text_add_user")}
                    type="submit"
                    className="bg-blue-600 text-white hover:bg-blue-700"
                    disabled={isLoading || teamsupdating}
                  />
                  {isEdit && (
                    <div className="flex items-center space-x-4 ">
                      <button
                        type="button"
                        onClick={toggleActive}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200 ease-in-out focus:outline-none ${formData.isActive
                          ? "bg-green-500 text-white hover:bg-green-600"
                          : "bg-red-500 text-white hover:bg-red-600"
                          }`}
                      >
                        {formData.isActive ? "Active" : " Mark Inactive"}
                      </button>
                    </div>

                  )}

                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

